import { useState, useEffect } from "react";

const supportsKeyboardEnv =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("padding-bottom: env(keyboard-inset-height)");

const NOTE_BORDER = "1.5px solid rgba(180, 160, 210, 0.5)";

function DailyNoteCard({ appTheme, isMobile, dateKey, note, onSave, onDelete, planningMode = "manual" }) {
  const [showSheet,       setShowSheet]       = useState(false);
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  const [draft,           setDraft]           = useState("");
  const [sheetOffset,     setSheetOffset]     = useState(0);

  // Keyboard avoidance — visualViewport fallback (same pattern as ManualEntryModal)
  useEffect(() => {
    if (!showSheet || supportsKeyboardEnv) return;
    function handleViewport() {
      const vv = window.visualViewport;
      if (!vv) return;
      setSheetOffset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    }
    window.visualViewport?.addEventListener("resize", handleViewport);
    window.visualViewport?.addEventListener("scroll", handleViewport);
    return () => {
      window.visualViewport?.removeEventListener("resize", handleViewport);
      window.visualViewport?.removeEventListener("scroll", handleViewport);
      setSheetOffset(0);
    };
  }, [showSheet]);

  function openSheet() {
    setDraft(note);
    setShowSheet(true);
  }

  function handleSave() {
    onSave(dateKey, draft.trim(), planningMode);
    setShowSheet(false);
  }

  function handleDelete() {
    onDelete(dateKey);
    setShowDeleteSheet(false);
  }

  const canSave = draft.trim().length > 0;
  const dividerColor = appTheme.cardBorder.replace("1px solid ", "");

  const iconBtnStyle = {
    width: "30px", height: "30px", borderRadius: "999px",
    border: "none", background: "transparent", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 0, flexShrink: 0,
  };

  const sheetStyle = {
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 3001,
    maxWidth: "560px", margin: "0 auto",
    borderRadius: "24px 24px 0 0",
    background: appTheme.pageBackground,
    boxShadow: "0 -8px 40px rgba(15,23,42,0.14)",
    animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
    display: "flex", flexDirection: "column",
    willChange: "transform",
    ...(supportsKeyboardEnv
      ? { paddingBottom: "env(keyboard-inset-height, 0px)" }
      : {
          transform: `translateY(-${sheetOffset}px)`,
          transition: sheetOffset > 0 ? "transform 0.15s ease" : "none",
        }
    ),
  };

  const backdropStyle = {
    position: "fixed", inset: 0, zIndex: 3000,
    background: "rgba(15,23,42,0.45)",
    backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
  };

  return (
    <>
      {/* ── CARD ───────────────────────────────────────────────────────────── */}

      {!note ? (
        /* Empty state */
        <button
          onClick={openSheet}
          style={{
            width: "100%", border: NOTE_BORDER, borderRadius: "20px",
            background: appTheme.cardBackground,
            boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
            padding: "14px 16px", cursor: "pointer",
            display: "flex", flexDirection: "row",
            alignItems: "center", justifyContent: "center", gap: "8px",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke={appTheme.mutedText} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span style={{ fontSize: "13px", color: appTheme.mutedText, fontFamily: appTheme.fontBody, fontWeight: "400" }}>
            Add a note for today
          </span>
        </button>
      ) : (
        /* Note exists */
        <div style={{
          background: appTheme.cardBackground, border: NOTE_BORDER,
          borderRadius: "20px", boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 12px 12px 16px" }}>
            <span style={{ fontSize: "14px", fontWeight: "500", fontFamily: appTheme.fontBody, color: appTheme.primaryText }}>
              Daily Note
            </span>
            <div style={{ display: "flex", gap: "2px" }}>
              <button
                onClick={openSheet}
                style={{ ...iconBtnStyle, color: appTheme.secondaryText }}
                aria-label="Edit note"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowDeleteSheet(true); }}
                style={{ ...iconBtnStyle, color: appTheme.mutedText }}
                aria-label="Delete note"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: dividerColor, margin: "0 16px" }} />

          {/* Note preview — tapping opens sheet */}
          <button
            onClick={openSheet}
            style={{ width: "100%", border: "none", background: "transparent", cursor: "pointer", padding: "12px 16px 14px", textAlign: "left" }}
          >
            <div style={{
              fontSize: "13px", fontFamily: appTheme.fontBody,
              color: appTheme.secondaryText, lineHeight: 1.55,
              display: "-webkit-box", WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
              {note}
            </div>
          </button>
        </div>
      )}

      {/* ── EDIT BOTTOM SHEET ──────────────────────────────────────────────── */}

      {showSheet && (
        <>
          <div onClick={() => setShowSheet(false)} style={backdropStyle} />
          <div onClick={(e) => e.stopPropagation()} style={sheetStyle}>

            {/* Drag handle */}
            <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "4px" }}>
              <div style={{ width: "36px", height: "4px", borderRadius: "999px", background: dividerColor }} />
            </div>

            {/* Title */}
            <div style={{ padding: "10px 20px 14px" }}>
              <div style={{
                fontSize: isMobile ? "20px" : "22px", fontWeight: "600",
                fontFamily: appTheme.fontHeading, color: appTheme.primaryText,
              }}>
                Daily Note
              </div>
            </div>

            {/* Textarea */}
            <div style={{ padding: "0 20px" }}>
              <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onFocus={(e) => { const l = e.target.value.length; e.target.setSelectionRange(l, l); }}
                placeholder="How did today go? Any thoughts on your meals..."
                rows={5}
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "14px", borderRadius: "16px",
                  border: NOTE_BORDER,
                  background: appTheme.inputBackground,
                  fontSize: "14px", color: appTheme.primaryText,
                  lineHeight: 1.6, resize: "none", outline: "none",
                  fontFamily: appTheme.fontBody,
                }}
              />
            </div>

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px 24px" }}>
              <span style={{ fontSize: "12px", color: appTheme.mutedText, fontFamily: appTheme.fontBody }}>
                {draft.length} characters
              </span>
              <button
                onClick={handleSave}
                style={{
                  padding: "8px 20px", borderRadius: "999px",
                  border: canSave ? appTheme.accentBorder : appTheme.pillBorder,
                  background: canSave ? appTheme.accentColor : appTheme.pillBackground,
                  color: canSave ? "#ffffff" : appTheme.mutedText,
                  fontFamily: appTheme.fontBody, fontSize: "13px", fontWeight: "700",
                  cursor: canSave ? "pointer" : "default",
                  transition: "all 0.15s ease",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── DELETE CONFIRMATION SHEET ──────────────────────────────────────── */}

      {showDeleteSheet && (
        <>
          <div onClick={() => setShowDeleteSheet(false)} style={backdropStyle} />
          <div style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 3001,
            background: appTheme.pageBackground,
            borderRadius: "24px 24px 0 0",
            padding: isMobile ? "24px 20px 44px" : "28px 28px 40px",
            animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          }}>
            <div style={{ fontFamily: appTheme.fontHeading, fontSize: "20px", fontWeight: "600", color: appTheme.primaryText, marginBottom: "6px" }}>
              Delete this note?
            </div>
            <div style={{ fontFamily: appTheme.fontBody, fontSize: "14px", color: appTheme.secondaryText, marginBottom: "24px" }}>
              This cannot be undone.
            </div>
            <div style={{ display: "grid", gap: "10px" }}>
              <button
                onClick={handleDelete}
                style={{
                  width: "100%", padding: "15px", borderRadius: "14px", border: "none",
                  background: "#DC2626", color: "#fff",
                  fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "700", cursor: "pointer",
                }}
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteSheet(false)}
                style={{
                  width: "100%", padding: "15px", borderRadius: "14px",
                  border: appTheme.cardBorder, background: appTheme.cardBackground,
                  fontFamily: appTheme.fontBody, fontSize: "15px", fontWeight: "600",
                  color: appTheme.primaryText, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default DailyNoteCard;
